import type { CharterEmailKind } from '@sea-snag/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { adminApi } from '../lib/api-client';
import { queryKeys, STALE_TIMES } from '../lib/query-keys';
import { clearOrganization, getOrganization, removeRecentAdminCompany } from '../stores/org-store';

export function useAdminDashboard() {
  return useQuery({
    queryKey: queryKeys.admin.dashboard(),
    queryFn: adminApi.dashboard,
    staleTime: STALE_TIMES.MEDIUM,
  });
}

export function useAdminCompanies(params: {
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: string;
}) {
  const searchParams: Record<string, string | undefined> = {
    search: params.search || undefined,
    page: params.page?.toString(),
    limit: params.limit?.toString(),
    sortBy: params.sortBy,
    sortOrder: params.sortOrder,
  };

  return useQuery({
    queryKey: queryKeys.admin.companies(params),
    queryFn: () => adminApi.companies(searchParams),
    staleTime: STALE_TIMES.MEDIUM,
  });
}

export function useAdminCompanySearch(q: string) {
  return useQuery({
    queryKey: queryKeys.admin.companySearch(q),
    queryFn: () => adminApi.searchCompanies(q),
    enabled: q.length >= 2,
    staleTime: STALE_TIMES.MEDIUM,
  });
}

export function useAdminUsers(params: { search?: string; page?: number; limit?: number }) {
  const searchParams: Record<string, string | undefined> = {
    search: params.search || undefined,
    page: params.page?.toString(),
    limit: params.limit?.toString(),
  };

  return useQuery({
    queryKey: queryKeys.admin.users(params),
    queryFn: () => adminApi.users(searchParams),
    staleTime: STALE_TIMES.MEDIUM,
  });
}

export function useAdminCompanyDeletionPreview(pid: string | null) {
  return useQuery({
    queryKey: queryKeys.admin.companyDeletionPreview(pid ?? ''),
    queryFn: () => adminApi.companyDeletionPreview(pid ?? ''),
    enabled: pid !== null,
    // Always recount when the dialog opens — this guards an irreversible action.
    staleTime: 0,
    gcTime: 0,
  });
}

export function useDeleteAdminCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (pid: string) => adminApi.deleteCompany(pid),
    onSuccess: (_data, pid) => {
      removeRecentAdminCompany(pid);
      if (getOrganization()?.pid === pid) clearOrganization();
      queryClient.removeQueries({ queryKey: queryKeys.admin.companyDeletionPreview(pid) });
      queryClient.removeQueries({ queryKey: queryKeys.admin.companyDetail(pid), exact: true });
    },
    // On failure too: a timed-out request may still have deleted the company.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.admin.all });
    },
  });
}

export function useAdminCharters(params: {
  search?: string;
  country?: string;
  invited?: string;
  gold?: string;
  page?: number;
  limit?: number;
}) {
  const searchParams: Record<string, string | undefined> = {
    search: params.search || undefined,
    country: params.country || undefined,
    invited: params.invited && params.invited !== 'all' ? params.invited : undefined,
    gold: params.gold && params.gold !== 'all' ? params.gold : undefined,
    page: params.page?.toString(),
    limit: params.limit?.toString(),
  };

  return useQuery({
    queryKey: queryKeys.admin.charters(params),
    queryFn: () => adminApi.charters(searchParams),
    staleTime: STALE_TIMES.SHORT,
  });
}

export function useCharterCountries() {
  return useQuery({
    queryKey: queryKeys.admin.charterCountries(),
    queryFn: adminApi.charterCountries,
    staleTime: STALE_TIMES.LONG,
  });
}

export function useSetCharterNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ pid, note }: { pid: string; note: string | null }) =>
      adminApi.setCharterNote(pid, note),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'charters'] });
    },
  });
}

export function useSetCharterCalled() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ pid, called }: { pid: string; called: boolean }) =>
      adminApi.setCharterCalled(pid, called),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'charters'] });
    },
  });
}

/** Sends a real marketing email to a real company — always behind a confirmation. */
export function useSendCharterEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ pid, kind }: { pid: string; kind: CharterEmailKind }) =>
      adminApi.sendCharterEmail(pid, kind),
    // A failed or refused attempt is recorded too, so the list refreshes either way.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'charters'] });
    },
  });
}
